import type { FormControlSetting } from '@/app/editor/iframe/atom/Atom.model';

/**
 * Get all settings that every button has
 * @param targetSelector targetSelector
 * @param options options
 * @param options.isDynamicModule isDynamicModule
 * @returns FormControlSettings
 */
export const getCommonButtonFormControlSettings = (
  targetSelector: string,
  options: {
    isDynamicModule?: boolean;
  },
): FormControlSetting[] => {
  const settings: FormControlSetting[] = [
    {
      // edit button text content
      type: 'html:muti',
      name: 'btnText',
      targetSelectors: options.isDynamicModule
        ? [`${targetSelector} > span`]
        : [`${targetSelector} > span`, targetSelector],
      property: '',
    },
    {
      type: 'html:extraClass',
      name: options.isDynamicModule ? 'extraClass' : 'extra_class',
      targetSelector: targetSelector,
      property: 'data-exc',
      default_value: '',
    },
    {
      // edit button text font size
      type: 'css',
      name: 'font-size',
      unit: 'px',
      targetSelector: targetSelector,
      attrStoredSelector: options.isDynamicModule ? '.module' : undefined,
      property: 'font-size',
      require: true,
      selfSelector: '-1,1',
    },
    {
      type: 'css',
      name: 'font',
      targetSelector: targetSelector,
      attrStoredSelector: options.isDynamicModule ? '.module' : undefined,
      property: 'font-family',
      require: true,
      selfSelector: '-1,1',
    },
    {
      type: 'css',
      name: options.isDynamicModule ? 'texttransform' : 'textTransform',
      targetSelector: targetSelector,
      attrStoredSelector: options.isDynamicModule ? '.module' : undefined,
      property: 'text-transform',
      require: true,
      selfSelector: '-1,1',
    },
    {
      type: 'css',
      name: 'button-color',
      fieldType: 'color',
      targetSelector: targetSelector,
      attrStoredSelector: options.isDynamicModule ? '.module' : undefined,
      property: 'background-color',
      require: true,
      selfSelector: '-1,1',
    },
    {
      type: 'css',
      name: options.isDynamicModule ? 'color' : 'text-color',
      fieldType: 'color',
      targetSelector: targetSelector,
      attrStoredSelector: options.isDynamicModule ? '.module' : undefined,
      property: 'color',
      require: true,
      selfSelector: '-1,1',
    },
    {
      type: 'css',
      name: options.isDynamicModule ? 'button-color-horver' : 'button-color-hover',
      targetSelector: `${targetSelector}:hover`,
      attrStoredSelector: options.isDynamicModule ? '.module' : undefined,
      fieldType: 'color',
      property: 'background-color',
      selfSelector: '1,1',
    },
    {
      type: 'css',
      name: options.isDynamicModule ? 'color-horver' : 'text-color-hover',
      targetSelector: `${targetSelector}:hover`,
      attrStoredSelector: options.isDynamicModule ? '.module' : undefined,
      fieldType: 'color',
      property: 'color',
      selfSelector: '1,1',
    },
    {
      type: 'html:attr',
      name: 'stretch_lg',
      targetSelector: options.isDynamicModule ? '.module' : '.elm',
      property: 'data-stretch-lg',
    },
    {
      type: 'html:attr',
      name: 'stretch_md',
      targetSelector: options.isDynamicModule ? '.module' : '.elm',
      property: 'data-stretch-md',
    },
    {
      type: 'html:attr',
      name: 'stretch_sm',
      targetSelector: options.isDynamicModule ? '.module' : '.elm',
      property: 'data-stretch-sm',
    },
    {
      type: 'html:attr',
      name: 'stretch_xs',
      targetSelector: options.isDynamicModule ? '.module' : '.elm',
      property: 'data-stretch-xs',
    },
    {
      type: 'css',
      name: 'width',
      targetSelector: targetSelector,
      attrStoredSelector: options.isDynamicModule ? '.module' : undefined,
      property: 'width',
      unit: '%',
      require: true,
      selfSelector: '-1,1',
    },
    {
      type: 'css',
      name: 'height',
      targetSelector: targetSelector,
      attrStoredSelector: options.isDynamicModule ? '.module' : undefined,
      property: 'height',
      unit: 'px',
      require: true,
      selfSelector: '-1,1',
    },
    {
      type: 'custom:align',
      name: 'align_lg',
      targetSelector: options.isDynamicModule ? '.module' : '.elm',
      attrStoredSelector: options.isDynamicModule ? '.module' : undefined,
      property: '',
    },
    {
      type: 'custom:align',
      name: 'align_md',
      targetSelector: options.isDynamicModule ? '.module' : '.elm',
      attrStoredSelector: options.isDynamicModule ? '.module' : undefined,
      property: '',
    },
    {
      type: 'custom:align',
      name: 'align_sm',
      targetSelector: options.isDynamicModule ? '.module' : '.elm',
      attrStoredSelector: options.isDynamicModule ? '.module' : undefined,
      property: '',
    },
    {
      type: 'custom:align',
      name: 'align_xs',
      targetSelector: options.isDynamicModule ? '.module' : '.elm',
      attrStoredSelector: options.isDynamicModule ? '.module' : undefined,
      property: '',
    },
  ];

  if (options.isDynamicModule) {
    settings.push({
      type: 'css:font-style',
      name: 'font-style',
      targetSelector: targetSelector,
      property: '',
    });
  }

  return settings;
};

/**
 * getRequiredSettingDefaultValue
 * @param $atom $atom
 * @param settings ettings
 */
export const getRequiredSettingDefaultValue = ($atom: JQuery, settings: FormControlSetting[]) => {
  settings.forEach((setting) => {
    if (setting.require) {
      const $targetSelector = $atom.find(setting.targetSelector as string);
      const defaultValue = $targetSelector.css(setting.property as string);
      setting['default_value'] = defaultValue;
    }
  });
};
